import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from "react-query";
import { useLocation } from 'react-router-dom'
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { useTable } from 'react-table';
import { AppContext } from "../AppContext";
import { Modal, Button, notification, Popover, Spin, Tooltip } from 'antd';
import './WorkOrders.css';
import tr from '../i18n';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { SearchOutlined, PlusCircleOutlined, InfoCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerIcon from "../assets/map_marker.png"
import MapsIcon from "../assets/gmaps.png"
import { BASE_MAPS, MAP_ATTRIBUTION_URL } from "../utils/MapUtils";
import WorkOrderModal from '../components/WorkOrderModal';
import { hasAccess, hasRoles, ROLES } from "../utils/AccessLevels";


// Fix for the default marker icon not showing
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const WorkOrderTable = () => {
  const woReportDataRef = useRef([]);
  const userDataRef = useRef([]);
  const [tenantId, setTenantId] = useState('');
  const [woReportData, setWoReportData] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [gardenFilter, setGardenFilter] = useState('');
  const [isAsgGalleryOpen, setIsAsgGalleryOpen] = useState(false);
  const [isWorkGalleryOpen, setIsWorkGalleryOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [newWorkModalVisibility, setNewWorkModalVisibility] = useState(false);
  const [workDetailsModalVisibility, setWorkDetailsModalVisibility] = useState(false);
  const [workMapVisibility, setWorkMapVisibility] = useState(false);
  const [activeWork, setActiveWork] = useState(null);
  const [activeMarkers, setActiveMarkers] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [userData, setUserData] = useState(null);
  const [refreshTable, setRefreshTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const { gardens, fetchAllGardens, role } = useContext(AppContext);
  const [mapCenter, setMapCenter] = useState([41.084, 29.142205]);
  const [selectedTileUrl, setSelectedTileUrl] = useState(BASE_MAPS[0].value);
  const navigate = useNavigate();
  const customMarker = new L.Icon({
    iconUrl: MarkerIcon,
    iconSize: [32, 32], // Set the size of the icon
    iconAnchor: null, // Set the point of the icon to be anchored (centered at the bottom)
    popupAnchor: [0, -32], // Set the point from which the popup will appear
  });
  const location = useLocation();
  const { initGardenId } = location.state || {};

  useEffect(() => {
    fetchAllGardens()
  }, [])

  useEffect(() => {
    if (!role) return;
    try {
      if (!hasAccess(ROLES.REGIONAL_MANAGER, role)) {
        navigate("/control-center/gardens");
      }
    } catch (error) {
      console.error(error);
    }
  }, [navigate, role]);

  function MapController({ visible, center, zoom = 13 }) {
    const map = useMap();

    useEffect(() => {
      if (visible && center) {
        setTimeout(() => {
          map.invalidateSize();
          map.setView(center, zoom, { animate: false });
        }, 0);
      }
    }, [visible, center, zoom, map]);

    return null;
  }

  const columns = useMemo(() => [
    { Header: tr('Title'), accessor: 'workTitle' },
    {
      Header: tr('Work Type'), accessor: 'workType',
      Cell: ({ row }) => renderWorkTypeCell(row),
    },
    {
      Header: tr('Status'),
      accessor: 'completed',
      Cell: ({ row }) => renderStatusCell(row),
    },
    { Header: tr('Garden Name'), accessor: 'gardenName' },
    { Header: tr('Creator'), accessor: 'creatorName' },
    { Header: tr('Creation Date'), accessor: 'assignDate' },
    {
      Header: tr('Details'),
      accessor: '_id',
      Cell: ({ row }) => {
        const [popoverVisible, setPopoverVisible] = useState(false);

        const handleRemove = () => {
          setPopoverVisible(false);
          if (row?.original?._id && userDataRef?.current?.id) {
            deleteWorkOrder.mutate({
              workOrderId: row.original._id,
              userId: userDataRef?.current?.id
            });
          }
        };

        return (
          <div className="flex flex-row gap-4 items-center">
            {/* Details button */}
            <Tooltip title={tr("Details")}>
              <Button
                type="text"
                icon={<InfoCircleOutlined style={{ fontSize: 18 }} />}
                onClick={() => handleCellClick(row.original._id, row)}
              />
            </Tooltip>

            {/* Remove button with popover */}
            <Popover
              content={
                <div className="p-3 flex flex-col items-center">
                  {tr("Are you sure you want to remove this work order?")}
                  <div>
                    <Button type="link" onClick={handleRemove}>
                      {tr("Yes")}
                    </Button>
                    <Button type="link" onClick={() => setPopoverVisible(false)}>
                      {tr("No")}
                    </Button>
                  </div>
                </div>
              }
              trigger="click"
              visible={popoverVisible}
              onVisibleChange={setPopoverVisible}
            >
              <Tooltip title={tr("Remove")}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: 18 }} />}
                />
              </Tooltip>
            </Popover>
          </div>
        );
      }
    },
  ], []);

  const userSettings = useQuery(["userSettings"], () =>
    axios.get(
      process.env.REACT_APP_GARDENER_API_END_POINT +
      `/user/settings`
    ), {
    onSuccess: (data) => {
      if (data?.data?.data?.id && data?.data?.data?.name) {
        setUserData({ id: data?.data?.data?.id, name: data?.data?.data?.name });
        userDataRef.current = { id: data?.data?.data?.id, name: data?.data?.data?.name };
      }
    },
    enabled: true,
    refetchOnWindowFocus: false
  });

  const fetchReportData = useMutation(data =>
    axios.post(`${process.env.REACT_APP_EMPLOYEE_API_END_POINT}/workorders/report`, data),
    {
      onMutate: () => setLoading(true),
      onSuccess: data => {
        if (data?.data?.success && data.data.data?.workOrders) {
          setWoReportData(data.data.data.workOrders);
          woReportDataRef.current = data.data.data.workOrders;
        }
      },
      onError: error => {
        console.error("Error fetching report data:", error);
      },
      onSettled: () => setLoading(false)
    }
  );

  useEffect(() => {
    const userSettings = localStorage.getItem("settings");
    setTenantId(JSON.parse(userSettings));
  }, []);

  useEffect(() => {
    if (!initGardenId || initGardenId === '0') return;

    setGardenFilter(initGardenId)

  }, [initGardenId])

  useEffect(() => {
    if (tenantId.title) {
      fetchReportData.mutate({ tenantId: tenantId.title, gardenId: gardenFilter });
    }
  }, [tenantId.title, gardenFilter]);


  const filteredWOData = useMemo(() => {
    const filtered = woReportData.filter(row =>
      row.workTitle.toLowerCase().includes(searchFilter.toLowerCase())
    );
    return filtered;
  }, [woReportData, searchFilter]);

  const handleCellClick = (workId, row) => {
    const selectedWork = woReportDataRef.current.find(work => work._id === workId);
    if (selectedWork) {
      setActiveWork(selectedWork);
      setActiveRow(row)
      setWorkDetailsModalVisibility(true);
    } else {
    }
  };

  const renderStatusCell = (row, largeTxt) => {
    const isCompleted = row.values.completed;
    const isStarted = row.original.workItems?.length > 0;
    let status = tr('Waiting');
    let iconColor = "#60626C";

    if (isStarted && !isCompleted) {
      iconColor = "#FF9500";
      status = tr('In Progress');
    } else if (isCompleted) {
      iconColor = "#34C759";
      status = tr('Completed');
    }

    return (
      <div className="flex flex-row items-center gap-2">
        {status}
        <div className="circular-icon" style={{ backgroundColor: iconColor }} />
      </div>
    );
  };

  const renderWorkTypeCell = (row) => {
    const workType = translateWorkType(row.values.workType) || row.values.workType;
    return (
      <span> {workType}</span>
    );
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: filteredWOData,
  });

  const createWorkOrder = useMutation(data =>
    axios.post(`${process.env.REACT_APP_EMPLOYEE_API_END_POINT}/workorders/create`, data),
    {
      onMutate: () => setLoading(true),
      onSuccess: data => {
        notification.success({
          message: tr('Work Order Added')
        });
        if (tenantId.title) {
          fetchReportData.mutate({ tenantId: tenantId.title, gardenId: gardenFilter });
        }
      },
      onError: error => {
        console.error("Error creating work order:", error);
      },
      onSettled: () => setLoading(false)
    }
  );

  const deleteWorkOrder = useMutation(data =>
    axios.post(`${process.env.REACT_APP_EMPLOYEE_API_END_POINT}/workorders/delete`, data),
    {
      onMutate: () => setLoading(true),
      onSuccess: data => {
        if (data && data.data.success === true) {
          notification.success({
            message: tr('Work Order Deleted')
          })
          if (tenantId.title) {
            fetchReportData.mutate({ tenantId: tenantId.title, gardenId: gardenFilter });
          }
        }
        else if (data && data.data.success === false && data.data.code === 409) {
          notification.error({
            message: tr('You are unauthorized to delete the work order')
          })
        }
        else {
          notification.error({
            message: `Error deleting work order`
          })
        }
      },
      onError: error => {
        notification.error({
          message: `Error deleting work order: ${error}`
        })
      },
      onSettled: () => setLoading(false)
    }
  );

  const handleWOCreation = (data) => {
    data.tenantId = tenantId?.title
    data.creatorId = userDataRef?.current?.id || 11
    data.creatorName = userDataRef?.current?.name || 'Admin'
    createWorkOrder.mutateAsync(data);
    setNewWorkModalVisibility(false);
  };

  return (
    <div className="table-wrapper">
      <Spin spinning={loading} tip={tr('Loading')}>
        <div className="filter-container flex flex-row justify-between items-center">
          <div className='flex flex-row gap-3'>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => { setSearchFilter(e.target.value) }}
              placeholder={tr('Search')}
              className="search-bar"
            />
            <select
              value={gardenFilter}
              onChange={(e) => { setGardenFilter(e.target.value) }}
              className="dropdown"
            >
              <option value="">{tr('All Gardens')}</option>
              {gardens.map((garden, index) => (
                <option key={index} value={garden.id}>
                  {garden.name}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <button className="flex flex-row p-4 gap-2 h-9 rounded-xl text-sm font-semibold text-black border items-center text-center justify-center" onClick={() => {
              setNewWorkModalVisibility(true);
            }}>
              <PlusCircleOutlined size={30} className="items-center"></PlusCircleOutlined> {tr('New Work Order')}</button>
          </div>
        </div>

        <table {...getTableProps()} className="styled-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="table-row">
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <Modal
          title={tr('Work Order Details')}
          visible={workDetailsModalVisibility}
          onCancel={() => setWorkDetailsModalVisibility(false)}
          footer={[
            <Button key="close" onClick={() => setWorkDetailsModalVisibility(false)}>
              {tr('Close')}
            </Button>,
          ]}
          width="30%"
          style={{ top: 60 }}
          bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          {activeWork &&
            <div className="modal-content">
              <div className='flex flex-col border rounded-md p-4 gap-2'>
                <div className='flex flex-row w-full text-lg justify-center'>
                  {tr('Work Definition')}
                </div>

                <div>
                  {tr('Title')}: {activeWork?.workTitle}
                </div>

                {activeRow &&
                  <div className='flex flex-row items-center gap-2'>
                    <div >{tr('Status')}:</div>
                    {renderStatusCell(activeRow)}
                  </div>
                }

                {
                  <div className='flex flex-row items-center gap-2'>
                    <div >{tr('Work Type')}:</div>
                    {translateWorkType(activeWork?.workType) || activeWork?.workType}
                  </div>
                }

                {activeWork?.assignmentNotes?.some(str => str !== "") && (
                  <div className='flex flex-row items-center gap-2'>
                    <div>{tr('Notes')}:</div>
                    {activeWork?.assignmentNotes.join(', ')}
                  </div>
                )}

                {
                  <div className='flex flex-row items-center gap-2'>
                    <div >{tr('Creator')}:</div>
                    {activeWork?.creatorName}
                  </div>
                }

                {
                  <div className='flex flex-row items-center gap-2'>
                    <div >{tr('Creation Date')}:</div>
                    {activeWork?.assignDate}
                  </div>
                }

                {activeWork?.assignmentImages?.some(str => str !== "") &&
                  <>
                    <div className='flex flex-row'>

                      {activeWork.assignmentImages.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          onClick={() => {
                            setIsAsgGalleryOpen(true);
                            setPhotoIndex(index);
                          }}
                          style={{ width: '100px', cursor: 'pointer', margin: '10px' }}
                          alt={`Thumbnail ${index + 1}`}
                        />
                      ))}
                    </div>

                    {isAsgGalleryOpen && (
                      <Lightbox
                        mainSrc={activeWork?.assignmentImages[photoIndex]}
                        nextSrc={activeWork?.assignmentImages[(photoIndex + 1) % activeWork?.assignmentImages.length]}
                        prevSrc={activeWork?.assignmentImages[(photoIndex + activeWork?.assignmentImages.length - 1) % activeWork?.assignmentImages.length]}
                        onCloseRequest={() => setIsAsgGalleryOpen(false)}
                        onMovePrevRequest={() =>
                          setPhotoIndex((photoIndex + activeWork?.assignmentImages.length - 1) % activeWork?.assignmentImages.length)
                        }
                        onMoveNextRequest={() =>
                          setPhotoIndex((photoIndex + 1) % activeWork?.assignmentImages.length)
                        }
                      />
                    )}
                  </>
                }

              </div>

              {activeWork?.workItems && activeWork.workItems.length > 0 &&
                <>
                  <div className='flex flex-col border rounded-md p-4 mt-4 gap-2'>
                    <div className='flex flex-row w-full text-lg justify-center'>
                      {tr('Work Progress')}
                    </div>

                    {
                      activeWork.workItems.map((wi =>
                        <>
                          <div className='flex flex-row'>
                            {tr('Employee')}: {wi?.userName}
                          </div>
                          <div className='flex flex-row'>
                            {tr('Start Time')}: {wi?.startDate}
                          </div>
                          {wi?.endDate && <div className='flex flex-row'>
                            {tr('End Time')}: {wi?.endDate}
                          </div>}
                          {wi.endTimeStamp && wi.endTimeStamp != "" && wi.startTimeStamp && wi.startTimeStamp != "" &&
                            <div className='flex flex-row'>
                              {tr('Work Duration')}: {formatTimeDifference(wi.startTimeStamp, wi.endTimeStamp)}
                            </div>}
                          {wi?.resultNotes?.some(str => str !== "") && <div className='flex flex-row'>
                            {tr('Employee Notes')}: {wi?.resultNotes?.join(", ")}
                          </div>}
                          {wi.startLat != 0 && wi.startLng != 0 && <div className='flex flex-row gap-2'>
                            {tr('Start Location')}: {
                              <button onClick={() => {
                                setMapCenter([wi.startLat, wi.startLng]);
                                const markers = [{
                                  position: [wi.startLat, wi.startLng],
                                  description: tr('Start Location')
                                }];
                                setActiveMarkers(markers)
                                setWorkMapVisibility(true)
                              }}
                                style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                                <img src={MapsIcon} alt="button icon" style={{ width: '20px', height: '20px' }} />
                              </button>
                            }
                          </div>}
                          {wi.endLat && wi.endLat != 0 && wi.endLng && wi.endLng != 0 && <div className='flex flex-row gap-2'>
                            {tr('End Location')}: {
                              <button onClick={() => {
                                setMapCenter([wi.endLat, wi.endLng]);
                                const markers = [{
                                  position: [wi.endLat, wi.endLng],
                                  description: tr('End Location')
                                }];
                                setActiveMarkers(markers)
                                setWorkMapVisibility(true)
                              }}
                                style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                                <img src={MapsIcon} alt="button icon" style={{ width: '20px', height: '20px' }} />
                              </button>
                            }
                          </div>}
                          {wi?.resultImages?.some(str => str !== "") &&
                            <>
                              <div className='flex flex-row'>

                                {wi?.resultImages.map((image, index) => (
                                  <img
                                    key={index}
                                    src={image}
                                    onClick={() => {
                                      setIsWorkGalleryOpen(true);
                                      setPhotoIndex(index);
                                    }}
                                    style={{ width: '100px', cursor: 'pointer', margin: '10px' }}
                                    alt={`Thumbnail ${index + 1}`}
                                  />
                                ))}
                              </div>

                              {isWorkGalleryOpen && (
                                <Lightbox
                                  mainSrc={wi?.resultImages[photoIndex]}
                                  nextSrc={wi?.resultImages[(photoIndex + 1) % wi?.resultImages.length]}
                                  prevSrc={wi?.resultImages[(photoIndex + wi?.resultImages.length - 1) % wi?.resultImages.length]}
                                  onCloseRequest={() => setIsWorkGalleryOpen(false)}
                                  onMovePrevRequest={() =>
                                    setPhotoIndex((photoIndex + wi?.resultImages.length - 1) % wi?.resultImages.length)
                                  }
                                  onMoveNextRequest={() =>
                                    setPhotoIndex((photoIndex + 1) % wi?.resultImages.length)
                                  }
                                />
                              )}
                            </>
                          }
                        </>
                      ))
                    }
                  </div>
                </>}
            </div>
          }
        </Modal>

        <Modal
          title={tr('Work Location')}
          visible={workMapVisibility}
          onCancel={() => {
            setWorkMapVisibility(false)
          }}
          footer={[
            <Button key="close" onClick={() => setWorkMapVisibility(false)}>
              {tr('Close')}
            </Button>,
          ]}
          width="50%"
          style={{ top: 60 }}
          bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          <>
            {activeMarkers != null &&
              <>
                <MapContainer center={mapCenter} zoom={13} style={{ height: '400px', width: '100%' }}>
                  <MapController
                    visible={workMapVisibility}
                    center={mapCenter}
                    zoom={13}
                  />
                  <TileLayer
                    url={selectedTileUrl}
                    attribution={MAP_ATTRIBUTION_URL}
                  />
                  {activeMarkers.map((marker, index) => (
                    <>
                      {marker.position &&
                        <Marker key={index} position={marker.position} icon={customMarker}>
                          <Popup>{marker.description}</Popup>
                        </Marker>}
                    </>
                  ))}
                </MapContainer>
              </>
            }
          </>
        </Modal>

        <WorkOrderModal
          isOpen={newWorkModalVisibility}
          onClose={() => {
            setNewWorkModalVisibility(false);
          }}
          onSubmit={handleWOCreation}
        >
        </WorkOrderModal>
      </Spin>
    </div>
  );
};

function translateWorkType(workType) {
  const locale = localStorage.getItem("locale") || "en";
  if (locale === "tr") return workType;
  else {
    switch (workType) {
      case "kontrol":
        return "Control";
      case "bakım":
        return "Maintenance";
      case "tamir":
        return "Repair";
      case "imalat":
        return "Production";
      case "bilgi":
        return "Information";
      default:
        return workType;
    }
  }
}

function formatTimeDifference(startTime, endTime) {
  const diffInMs = endTime - startTime;

  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Build the result string conditionally to skip zero values
  const parts = [];
  if (hours > 0) parts.push(`${hours} ${tr('Hours')}`);
  if (minutes > 0) parts.push(`${minutes} ${tr('Minutes')}`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds} ${tr('Seconds')}`); // Include seconds even if it's 0, if it's the only part

  return parts.join(' ');
}

export default WorkOrderTable;